let amapSdkPromise = null

function buildPluginQuery(plugins) {
  const pluginList = (plugins || []).filter(Boolean)
  return pluginList.length ? '&plugin=' + encodeURIComponent(pluginList.join(',')) : ''
}

export function loadAmapSdk(key, plugins) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('当前环境不支持加载高德地图'))
  }

  if (window.AMap) {
    return Promise.resolve(window.AMap)
  }

  if (amapSdkPromise) {
    return amapSdkPromise
  }

  amapSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://webapi.amap.com/maps?v=2.0&key=' + encodeURIComponent(key) + buildPluginQuery(plugins)

    script.onload = function () {
      if (window.AMap) {
        resolve(window.AMap)
      } else {
        reject(new Error('高德地图 SDK 加载失败'))
      }
    }

    script.onerror = function () {
      reject(new Error('高德地图 SDK 加载失败'))
    }

    document.head.appendChild(script)
  })

  return amapSdkPromise
}

export function queryAmapBusLineByName(AMap, options) {
  const keyword = options.keyword
  const city = options.city || '361100'
  const pageSize = options.pageSize || 10
  const timeout = options.timeout || 8000

  return new Promise((resolve, reject) => {
    let isSettled = false
    const lineSearch = new AMap.LineSearch({
      city: city,
      pageIndex: 1,
      pageSize: pageSize,
      extensions: 'all'
    })

    const timer = setTimeout(function () {
      if (isSettled) {
        return
      }
      isSettled = true
      reject(new Error('高德 LineSearch 查询超时'))
    }, timeout)

    lineSearch.search(keyword, function (status, result) {
      if (isSettled) {
        return
      }
      isSettled = true
      clearTimeout(timer)
      if (status === 'complete' && result) {
        const lines = extractLineSearchCandidates(result)
        resolve({
          raw: result,
          lines: lines
        })
        return
      }

      if (status === 'no_data') {
        resolve({
          raw: result,
          lines: []
        })
        return
      }

      const errorMessage = result && (result.info || result.message) ? (result.info || result.message) : '高德公交名称查询失败'
      reject(new Error(errorMessage))
    })
  })
}

export function queryAmapBusLineByNameWebService(options) {
  const key = options.key
  const keyword = options.keyword
  const city = options.city || '361100'
  const pageSize = options.pageSize || 10
  const url = 'https://restapi.amap.com/v3/bus/linename?key=' + encodeURIComponent(key) +
    '&keywords=' + encodeURIComponent(keyword) +
    '&city=' + encodeURIComponent(city) +
    '&offset=' + encodeURIComponent(pageSize) +
    '&page=1&extensions=all'

  return requestJson(url).then(result => {
    if (result && (result.status === '1' || result.code === '1' || result.result === 'true')) {
      return {
        raw: result,
        lines: extractLineSearchCandidates(result)
      }
    }
    const errorMessage = result && (result.info || result.message) ? (result.info || result.message) : '高德公交名称查询失败'
    throw new Error(errorMessage)
  })
}

function extractLineSearchCandidates(result) {
  if (!result) {
    return []
  }
  if (Array.isArray(result.lineInfo)) {
    return result.lineInfo
  }
  if (Array.isArray(result.lines)) {
    return result.lines
  }
  if (Array.isArray(result.buslines)) {
    return result.buslines
  }
  return []
}

export function queryAmapBusLineById(AMap, options) {
  const key = options.key
  const lineId = options.lineId
  const url = 'https://restapi.amap.com/v3/bus/lineid?key=' + encodeURIComponent(key) +
    '&id=' + encodeURIComponent(lineId) +
    '&extensions=all'

  return requestJson(url).then(result => {
    if (result && (result.status === '1' || result.code === '1' || result.result === 'true')) {
      return result
    }
    const errorMessage = result && (result.info || result.message) ? (result.info || result.message) : '高德公交线路查询失败'
    throw new Error(errorMessage)
  })
}

function requestJson(url) {
  if (typeof window !== 'undefined' && window.fetch) {
    return window.fetch(url).then(response => {
      if (!response.ok) {
        throw new Error('高德公交线路查询失败')
      }
      return response.json()
    })
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.onreadystatechange = function () {
      if (xhr.readyState !== 4) {
        return
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch (error) {
          reject(error)
        }
      } else {
        reject(new Error('高德公交线路查询失败'))
      }
    }
    xhr.onerror = function () {
      reject(new Error('高德公交线路查询失败'))
    }
    xhr.send()
  })
}

export function getPrimaryBusSegment(busLineResult) {
  if (busLineResult && busLineResult.segmentlist && busLineResult.segmentlist.length) {
    return busLineResult.segmentlist[0]
  }
  if (busLineResult && busLineResult.buslist && busLineResult.buslist.length) {
    const busItem = busLineResult.buslist[0]
    if (busItem.segmentlist && busItem.segmentlist.length) {
      return busItem.segmentlist[0]
    }
  }
  return null
}

export function getBusLineIdentifier(busLine) {
  if (!busLine) {
    return ''
  }
  return busLine.id || busLine.uid || busLine.busid || ''
}

export function parseBusLinePath(AMap, busLine) {
  const primarySegment = getPrimaryBusSegment(busLine)

  if (primarySegment && primarySegment.drivercoord) {
    const coords = primarySegment.drivercoord.split(',')
    const points = []
    for (let index = 0; index < coords.length; index += 2) {
      const lng = Number(coords[index])
      const lat = Number(coords[index + 1])
      if (!isNaN(lng) && !isNaN(lat)) {
        points.push(new AMap.LngLat(lng, lat))
      }
    }
    if (points.length) {
      return points
    }
  }

  if (busLine && busLine.polyline) {
    const points = busLine.polyline.split(';').map(item => {
      const location = item.split(',')
      return new AMap.LngLat(Number(location[0]), Number(location[1]))
    }).filter(point => !isNaN(point.lng) && !isNaN(point.lat))

    if (points.length) {
      return points
    }
  }

  const viaStops = getBusLineStations(busLine)
  return viaStops.map(item => new AMap.LngLat(item.lng, item.lat))
}

export function getBusLineStations(busLine) {
  const primarySegment = getPrimaryBusSegment(busLine)

  if (primarySegment && primarySegment.passdepotname) {
    const depotNames = primarySegment.passdepotname.split(' ').filter(Boolean)
    return depotNames.map((name, index) => {
      return {
        index: index + 1,
        name: name,
        lng: NaN,
        lat: NaN
      }
    })
  }

  const viaStops = busLine && busLine.via_stops ? busLine.via_stops : []
  return viaStops.map((item, index) => {
    const location = (item.location || '').split(',')
    return {
      index: index + 1,
      name: item.name,
      lng: Number(location[0]),
      lat: Number(location[1])
    }
  }).filter(item => !isNaN(item.lng) && !isNaN(item.lat))
}

export function getBusLineEndpoints(busLine) {
  const primarySegment = getPrimaryBusSegment(busLine)
  const startPoi = busLine && busLine.spoi ? busLine.spoi : null
  const endPoi = busLine && busLine.epoi ? busLine.epoi : null

  const endpoints = []
  if (startPoi && !isNaN(Number(startPoi.x)) && !isNaN(Number(startPoi.y))) {
    endpoints.push({
      type: 'start',
      name: primarySegment && primarySegment.startname ? primarySegment.startname : (startPoi.name || '起点'),
      lng: Number(startPoi.x),
      lat: Number(startPoi.y)
    })
  }
  if (endPoi && !isNaN(Number(endPoi.x)) && !isNaN(Number(endPoi.y))) {
    endpoints.push({
      type: 'end',
      name: primarySegment && primarySegment.endname ? primarySegment.endname : (endPoi.name || '终点'),
      lng: Number(endPoi.x),
      lat: Number(endPoi.y)
    })
  }
  return endpoints
}



// WEBPACK FOOTER //
// ./src/utils/amapBus.js